export default {
  main: {
    title: "Tonal & Channel Mapping",
  },
  drawer: {
    image: "Image",
    analysis: "Histogram",
    palette: "Palette",
    ai: "AI Insights",
  },
  card: {
    imageReference: "Image Reference",
    histogram: "Histogram",
    colorPalette: "Color Palette",
    aiInsights: "AI Insights",
  },
  tabs: {
    color: "Color",
    tone: "Tone",
  },
  ai: {
    guideTitle: "AI Insights",
    guideDescription:
      "Unlock deep insights into your images with AI analysis, including color breakdown, composition suggestions, and more.",
    analyze: "Analyze with AI",
    refresh: "Refresh Analysis",
    refreshTooltip: "Refresh Analysis",
    analysisFailed: "Analysis failed: {message}",
  },
  aiAnalysis: {
    severity: {
      minor: "Minor",
      moderate: "Moderate",
      major: "Major",
      unknown: "Unspecified",
    },
  },
  taskSummary: {
    title: "Task Summary",
    currentTask: "Current task: Original -> Target Style ({styleName})",
    currentTaskLabel: "Current task:",
    currentTaskSource: "Original",
    currentTaskTarget: "Target Style",
    unknownStyle: "Style not specified",
    keyGapsTitle: "Key Gaps",
    priorityActionsTitle: "Priority Actions",
    aiOverviewTitle: "Edit Advice Overview",
    aiDetailPreviewLabel: "Full Edit Advice",
    aiDetailCta: "View How to Edit",
    aiDetailDialogTitle: "Edit Advice",
    aiDetailTitle: "Edit Advice",
    aiDetailClose: "Close",
    upgradedVersion: "Updated Version",
    upgradeToView: "Upgrade to View New Version",
    aiDetailPendingBanner: "Generating new version, it will automatically replace the current result when ready",
    aiDetailPendingHint: "Currently showing the previous version. It will be replaced automatically when the new version is ready",
    aiDetailGenerating: "Generating new version analysis, please wait...",
    actions: {
      uploadResult: "Upload Edited Version",
      openGuide: "Decode Tour",
    },
  },
  finalReview: {
    title: "Final Review & Share",
    upload: {
      title: "Upload Edited Version",
      description:
        "Upload the edited image for this task to prepare Before/After preview and sharing export.",
      stateReady: "Edited version ready",
      stateEmpty: "No edited version for this task yet",
      template: "Template",
      beforeSource: "Before source",
    },
    templatePreview: {
      before: "Before",
      after: "After",
    },
    uploading: "Uploading image...",
    backToTask: "Back To Task",
    replaceAfter: "Replace After",
    tabs: {
      slider: "Slider",
      sideBySide: "Side by Side",
    },
    moodFrame: {
      label: "Mood Frame",
    },
    actions: {
      download: "Download Share Image",
      generating: "Generating...",
    },
  },
  aiTaskSummary: {
    title: "AI Task Overview",
    loading: "AI is generating the task overview...",
    placeholder:
      "AI overview is not ready yet. You can continue with the insights panel below.",
    temporaryIssue:
      "This analysis ran into a temporary issue. Please try again later.",
  },
  pending: {
    public: {
      title: "Analyzing your photo",
      description:
        "We are comparing it with the reference to break down composition, color distribution, and editing gaps. The result page will open automatically when ready.",
      primary: "Breaking down the image structure. Please wait…",
      stages: {
        preparing: "Preparing the analysis environment…",
        scanning: "Scanning photo structure and tonal hierarchy…",
        locating: "Locating gaps based on color distribution…",
        generating:
          "Generating targeted retouch suggestions from the reference…",
      },
    },
    internal: {
      titleLoading: "Loading analysis context...",
      titleReady: "Preparing analysis task",
      description:
        "The system is initializing the analysis environment. It will automatically proceed to the results page when ready.",
    },
    steps: {
      auth: "Verifying user identity",
      capability: "Loading feature configuration",
      unlock: "Restoring unlock state",
      analyzing: "Preparing analysis",
      crop_deciding: "Waiting for crop result",
      submitting: "Submitting analysis task",
    },
    recovery: {
      processedReupload:
        "Processed image resource has expired. The page will re-upload the processed image with current assets and retry.",
      originalReupload:
        "Original image resource has expired. Please return to the upload page to recreate analysis assets.",
      multipleAssets:
        "Analysis assets are incomplete. Please return to the upload page to prepare original and processed images.",
      manualRecovery:
        "The current analysis task cannot be automatically recovered. Please refresh the page or restart the analysis later.",
    },
    actions: {
      retryProcessed: "Re-upload processed image and submit",
      retrySubmit: "Resubmit analysis task",
    },
    errors: {
      insufficient_credits:
        "Insufficient credits. Analysis task was not executed.",
      history_quota_exceeded:
        "History quota limit reached. Analysis task was not executed.",
      payment_materialization_pending:
        "Payment status is still syncing. Please resubmit later.",
      invalid_upload_asset: "Upload asset is not ready. Please resubmit.",
      image_missing:
        "Analysis resource is missing. Please go back to the upload page and resubmit.",
      source_not_ready: "Analysis source is not ready. Please resubmit later.",
      processed_mismatch:
        "Processed image does not match the current source. Please regenerate and try again.",
      analysis_not_ready_for_execution:
        "Local analysis is not complete. The system will continue when ready.",
      analysis_subject_id_required:
        "Result sync failed. Please resubmit or refresh the page.",
      subject_not_found: "Server result is not available. Please resync later.",
      not_implemented:
        "The analysis endpoint is not available on the server. Please try again later.",
      network_timeout: "Network request timed out. Please try again later.",
      network_error:
        "Network error. Analysis task submission failed. Please retry.",
      server_error:
        "Server error. Analysis task submission failed. Please retry.",
      server_unavailable: "Server is temporarily unavailable. Please retry.",
      analysis_command_failed:
        "Analysis task submission failed. Please try again later.",
      fallback: "Analysis task submission failed. Please try again later.",
      bootstrap_error:
        "Analysis preparation failed. Please go back to the upload page and retry.",
    },
  },
  relation: {
    errors: {
      network_error: "Network error",
      retry_failed: "Operation failed",
      manual_fallback: "Manual fallback to legacy logic",
      not_found_max_attempt: "Exceeded auto-upload retry limit",
      not_found: "Resource not found",
      relation_missing_max_attempt: "Exceeded auto-create retry limit",
      relation_missing_failed: "Failed to create relation",
      relation_restore_max_attempt: "Exceeded auto-restore retry limit",
      relation_restore_failed: "Failed to restore relation",
      processed_mismatch_max_attempt: "Exceeded auto-overwrite retry limit",
      processed_mismatch_failed: "Failed to overwrite processed image",
      missing_relation_id: "Missing relation_id",
    },
  },
  guideFlow: {
    eyebrow: "Guide",
    title: "Decoding Your Photo, Step by Step",
    stepIndex: "Step {current} / {total}",
    actions: {
      back: "Back",
      next: "Next",
      complete: "Finish",
      exit: "Exit",
      skipToTasklayout: "Go To Task Overview",
    },
    aiGate: {
      title: "Comparative Analysis",
      description:
        "Analyzing your photo in depth and generating insights. You'll automatically proceed to the next step once it's ready.",
      waiting: "AI is still analyzing. Please wait…",
      timeout:
        "This is taking longer than expected. You can continue in task overview now and re-open the guide later.",
      timeoutDescription:
        "Wait timeout reached. You can move to task overview first and continue the guide later.",
      timeoutWithDuration:
        "Waited for more than {duration}. You can continue in task overview now and re-open the guide later.",
      temporaryIssue:
        "This analysis ran into a temporary issue. Please try again later.",
      duration: {
        minute: "{count} min",
        second: "{count} sec",
      },
      positive: {
        primary: "Breaking down the image structure. Please wait…",
        messages: {
          0: "Scanning photo structure and tonal hierarchy…",
          1: "Locating gaps based on color distribution…",
          2: "Generating targeted retouch suggestions from the reference…",
        },
      },
    },
    gap: {
      defaultTitle: "Gap Step {index}",
      fallbackDiagnosis: "No gap diagnosis is available for this step yet.",
      suggestionLabel: "Suggested action: ",
      evidenceLabel: "Evidence target: ",
      restrictedDescription:
        "Unlock this step to view the full diagnosis, action guidance, and evidence target.",
      dataReadingTitle: "Data Reading",
      evidenceAriaLabel: "Data interpretation",
      toggleEvidenceSummaryAriaLabel: "Expand or collapse data summary",
      evidenceGroupsHeader: "Current Dynamic Step evidence_groups",
      analysisMarkdown: {
        title: "AI Analysis Result",
        description:
          "This step temporarily displays the AI analysis markdown and will be replaced with finer-grained dynamic steps later.",
        empty:
          "The AI analysis content is not available yet. Please try again shortly.",
      },
      empty: {
        title: "No Displayable Gaps Detected",
        description:
          "No gap items are currently available for this task. You can finish the guide and move to task overview.",
      },
    },
    hueMapping: {
      emptyHint: "Select a hue range to see where it appears in both images.",
      selectedHint: "Now viewing: {label}",
      luminanceZoneLabel: "Luminance zone",
      globalPercentHint:
        "Hue percentages are global. Choose a luminance zone to constrain the preview mask to that zone.",
      zonePercentHint:
        "Hue percentages are calculated within the selected luminance zone.",
    },
    saturationMapping: {
      emptyHint:
        "Select a saturation level to see where it appears in both images.",
      selectedHint: "Now viewing: {label}",
      luminanceZoneLabel: "Luminance zone",
      globalPercentHint:
        "Percentages are global. Choose a luminance zone to constrain the preview mask to that zone.",
      zonePercentHint:
        "Percentages are calculated within the selected luminance zone.",
      uploadCard: "Saturation Distribution · Upload",
      referenceCard: "Saturation Distribution · Reference",
      buckets: {
        low: "Low saturation",
        muted: "Muted saturation",
        medium: "Medium saturation",
        vibrant: "Vibrant saturation",
        high: "High saturation",
      },
    },
    labAbMapping: {
      emptyHint:
        "Select an A or B channel bucket to inspect matching color-bias regions in both images.",
      selectedHint: "Now viewing: {label}",
      luminanceZoneLabel: "Luminance zone",
      globalPercentHint:
        "Percentages are global. Choose a luminance zone to constrain the preview mask to that zone.",
      zonePercentHint:
        "Percentages are calculated within the selected luminance zone.",
      channelA: {
        title: "A channel · Green ↔ Red/Magenta",
        buckets: [
          "Strong Green",
          "Slight Green",
          "Neutral",
          "Slight Red/Magenta",
          "Strong Red/Magenta",
        ],
      },
      channelB: {
        title: "B channel · Blue ↔ Yellow",
        buckets: [
          "Strong Blue",
          "Slight Blue",
          "Neutral",
          "Slight Yellow",
          "Strong Yellow",
        ],
      },
    },
    placeholders: {
      rgb: {
        title: "RGB Histogram Overview",
        description:
          "Compare upload vs reference RGB histogram and overlay to identify dominant channel shifts and overall color trend.",
      },
      luminosity: {
        title: "Luminosity Histogram Overview",
        description:
          "Compare luminosity distributions to quickly assess tonal hierarchy differences and potential exposure drift.",
      },
      mask: {
        title: "Zone Mapping",
        description:
          "Use mask zoning preview to build a spatial understanding before moving into targeted gap guidance.",
      },
      hueMapping: {
        title: "HUE Mapping",
        description:
          "Pick a hue sector from the dual hue rings and inspect where that hue appears in upload and reference masks.",
        luminanceZoneLabel: "Luminance zone",
        globalPercentHint:
          "Hue percentages are global. Choose a luminance zone to constrain the preview mask to that zone.",
        zonePercentHint:
          "Hue percentages are calculated within the selected luminance zone.",
      },
      clippingMapping: {
        title: "Highlight/Shadow Clipping Mapping",
        description:
          "Show clipped highlights and clipped shadows in upload/reference masks using the luminance clipping thresholds.",
      },
      zoneBoxplotSnapshot: {
        title: "Zone Mask + Boxplot Snapshot",
        description:
          "Reuse the zone selector from the luminance mask panel and inspect the matching zone boxplot comparison below.",
      },
    },
  },
  atomicCapabilities: {
    title: "Insights Details",
    tabs: {
      image: "Image",
      tonalExposure: "Tonal & Exposure",
      globalColor: "Global Color",
      colorGrading: "Color Grading",
      aiInsights: "AI Insights",
    },
    image: {
      resolution: "Resolution",
      totalPixels: "Total Pixels",
      megapixels: "Megapixels",
      aspectRatio: "Aspect Ratio",
      fileFormat: "File Format",
      sourceLabel: "Upload source",
      activeBadge: "Active",
      previewAction: "Preview image: {subject}",
      previewAltOriginal: "Upload original preview",
      previewAltCropped: "Upload cropped preview",
      previewAltReference: "Reference preview",
      unavailable: "Image unavailable",
      referenceMissing: "Reference is missing",
      downloadCropped: "Download cropped",
    },
    statistics: {
      tonalExposureTitle: "Luminance Metrics (Global)",
      globalColorTitle: "Global Color Metrics (Global)",
      snapshotTitle: "Chromatic Profile",
      mean: "Mean",
      median: "Median",
      std: "Std",
      channels: {
        labL: "LAB L",
        hslL: "HSL L",
        hslH: "HSL H",
        hslS: "HSL S",
        labA: "LAB A",
        labB: "LAB B",
      },
    },
    colorGrading: {
      zoneLabel: "Zone Selector",
      labTitle: "LAB Statistics ({zone})",
      hslTitle: "HSL Statistics ({zone})",
      downloadLab: "Download LAB Statistics",
      downloadHsl: "Download HSL Statistics",
      empty: "No statistics available for this zone",
      tendencyTitle: "Zonal Tendency Notes",
      zone: {
        global: "Global",
        blacks: "Blacks",
        shadows: "Shadows",
        midtones: "Midtones",
        highlights: "Highlights",
        whites: "Whites",
      },
      tendency: {
        temperatureCool:
          "LAB B is below neutral, so this zone trends cooler (blue/cyan).",
        temperatureWarm:
          "LAB B is above neutral, so this zone trends warmer (yellow/orange).",
        temperatureNeutral:
          "LAB B mean is near neutral, so this zone has minimal warm/cool bias.",
        tintGreen:
          "LAB A is below neutral, indicating a slight green cast tendency.",
        tintMagenta:
          "LAB A is above neutral, indicating a slight magenta cast tendency.",
        tintNeutral:
          "LAB A mean is near neutral, so this zone has minimal green/magenta bias.",
        saturationLow: "HSL S is low, so this zone has weak saturation.",
        saturationMedium:
          "HSL S is mid-range, so this zone has medium saturation.",
        saturationHigh: "HSL S is high, so this zone has strong saturation.",
        luminanceDark: "HSL L is low, so this zone is overall darker.",
        luminanceNeutral:
          "HSL L is mid-range, so this zone has neutral luminance.",
        luminanceBright: "HSL L is high, so this zone is overall brighter.",
        hslLightnessTitle: "HSL lightness notes",
        hslHueTitle: "HSL hue notes",
        hslSaturationTitle: "HSL saturation notes",
        empty:
          "No statistics are available for this zone, so tendency notes cannot be generated yet.",
      },
    },
  },
  gapCards: {
    title: "Gaps",
    cardTitle: "Gap Cards",
    drawerOpen: "Expand gaps",
    drawerClose: "Collapse gaps",
    gapScore: "Gap score {score}",
    gapConclusion:
      "Current state: {current}, with a clear gap from target state: {target}.",
    actionSuggestion: "Recommended first action: {action}.",
    riskHint: "If over-processed, it may cause: {risk}.",
    viewAll: "Temp View All",
    viewAllDialogTitle: "Comparison Analysis Log (Temporary)",
    viewAllTabFull: "Full Log",
    viewAllTabProcessed: "Processed Log",
    viewAllTabHistogramAi: "Histogram AI Input",
    noLogData: "No comparison log data available yet.",
    noProcessedLogData: "No processed log data available yet.",
    noHistogramAiData: "No histogram AI input data available yet.",
    downloadJson: "Download JSON",
    expandEvidence: "View Details",
    viewDetails: "View details",
    evidenceComingSoon: "Details coming soon",
    untitledGap: "Gap {index}",
    labels: {
      current: "Current",
      target: "Target",
      source: "Source",
      reference: "Reference",
    },
    fallback: {
      tone: {
        title: "Exposure Structure Gap",
        currentState: "Highlights are harsh and shadow detail is compressed",
        targetState: "Highlights are softer with retained shadow texture",
        actionHint: "Recover highlights first, then lift blacks slightly",
        riskNote: "Too much highlight recovery can make the image look flat",
      },
      color: {
        title: "Overall Color Cast Gap",
        currentState: "Image is cool with slight cyan skin cast",
        targetState: "Image is neutral-warm with natural skin tone",
        actionHint: "Adjust white balance first, then reduce cyan bias",
        riskNote: "Pushing warmth too far can turn skin orange",
      },
      contrast: {
        title: "Contrast Rhythm Gap",
        currentState: "Midtones are crowded and contrast rhythm is flat",
        targetState: "Midtones are clearer with stronger tonal pacing",
        actionHint: "Open up midtones first, then set black/white bounds",
        riskNote: "Too much contrast can lose shadow detail",
      },
    },
  },
  gapDetail: {
    previousGap: "Previous",
    nextGap: "Next",
    gapSelectorLabel: "Switch gap",
  },
  compareStage: {
    title: "Dual Image Compare",
    subjectLabel: "Compare target",
    uploadViewLabel: "Upload image version",
    subjects: {
      upload: "Upload",
      reference: "Reference",
    },
    views: {
      original: "Original",
      processed: "Processed",
      cropped: "Cropped",
    },
    metrics: {
      exposureDelta: "Exposure",
      contrastDelta: "Contrast",
      temperatureDelta: "Temperature",
      saturationDelta: "Saturation",
    },
    errors: {
      referenceLoadFailed: "Reference image failed to load. You can retry.",
      referenceMissing: "Reference image is missing (context expired).",
    },
    actions: {
      retry: "Retry",
      downloadReference: "Download Reference",
      openEvidence: "Open Evidence",
      expand: "Expand",
      collapse: "Collapse",
    },
  },
  // 1-A：手机档（≤720）分段导航（四段：摘要 / 对照 / 缺口 / 洞察）。key 取通用含义，不绑定具体措辞。
  segments: {
    ariaLabel: "Result sections",
    summary: "Summary",
    compare: "Compare",
    gaps: "Gaps",
    insights: "Insights",
  },
  summaryBar: {
    targetLabel: "Target",
    gapsLabel: "Key gaps",
    gapsNone: "None",
    expand: "Show task summary",
    collapse: "Hide task summary",
  },
  imageDisplay: {
    original: "Original",
    analyzed: "Analyzed",
    borderCropping: "Border Cropping",
    downloadOriginal: "Download original",
    downloadProcessed: "Download processed",
    originalImage: "Original Image",
    originalImageFull: "Original Image full size",
    processedImage: "Processed Image",
    processedImageFull: "Processed Image full size",
  },
  featureGate: {
    titles: {
      unavailable_tier: "Your current plan does not support this feature",
      insufficient_credits: "Insufficient credits",
      locked: "This feature requires unlocking",
      default: "This feature is currently unavailable",
    },
    descriptions: {
      unavailable_tier: "Please upgrade your plan to continue.",
      insufficient_credits: "Please visit the console to top up or upgrade.",
      locked:
        "Confirm credit deduction to unlock this feature for the current image.",
      default: "Please try again later or visit the console.",
    },
    statusLabels: {
      unavailable_tier: "Plan Limit",
      insufficient_credits: "No Credits",
      locked: "Locked",
      default: "Unavailable",
    },
    primaryActions: {
      locked: "Unlock Now",
      need_confirm: "Unlock Now",
      upgrade_only: "Upgrade Plan",
      insufficient_credits: "Top Up / Upgrade",
      default: "Go to Console",
    },
    secondaryActions: {
      dismiss: "Dismiss",
      learn_more: "Learn More",
      later: "Maybe Later",
      locked: "Learn More",
      need_confirm: "Learn More",
      upgrade_only: "Learn More",
      insufficient_credits: "Maybe Later",
    },
  },
  confirmDialog: {
    cancel: "Cancel",
    creditsRequired: "Estimated credits",
    upgrade: {
      title: "Confirm AI Analysis Upgrade",
      currentVersion: "Current version",
      targetVersion: "Target version",
      executing: "Upgrading...",
      targetReady:
        "Target version results are ready. You can view them immediately after upgrading.",
      upgrading: "Upgrading",
      confirm: "Confirm Upgrade",
    },
    unlock: {
      title: "Confirm Unlock",
      balanceTotal: "Available credits",
      executing: "Unlocking...",
      unlocking: "Unlocking",
      confirm: "Confirm Unlock",
      messageZero: "This feature requires no credits. Continue to unlock?",
      messageSingular: "This feature requires 1 credit. Continue to unlock?",
      messagePlural:
        "This feature requires {credits} credits. Continue to unlock?",
      messageUnknown: "This feature requires credits. Continue to unlock?",
    },
  },
};
